import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// Tipos de resposta da API
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

// Configuração base do cliente API
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_TIMEOUT = 120000; // 120 segundos (sync com Tiny pode demorar)

// Criar instância do Axios
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  // Não definir Content-Type padrão - deixar o axios decidir baseado no tipo de dados
});

// Função para obter tokens do localStorage (compatível com Zustand persist)
const getAccessToken = (): string | null => {
  // Primeiro tentar pegar do Zustand persist
  const authStorage = localStorage.getItem('auth-storage');
  if (authStorage) {
    try {
      const parsed = JSON.parse(authStorage);
      if (parsed.state?.accessToken) {
        return parsed.state.accessToken;
      }
    } catch (e) {
      console.error('Erro ao ler token do Zustand:', e);
    }
  }
  
  // Fallback para chave direta (retrocompatibilidade)
  return localStorage.getItem('accessToken');
};

const getRefreshToken = (): string | null => {
  // Primeiro tentar pegar do Zustand persist
  const authStorage = localStorage.getItem('auth-storage');
  if (authStorage) {
    try {
      const parsed = JSON.parse(authStorage);
      if (parsed.state?.refreshToken) {
        return parsed.state.refreshToken;
      }
    } catch (e) {
      console.error('Erro ao ler refresh token do Zustand:', e);
    }
  }
  
  // Fallback para chave direta (retrocompatibilidade)
  return localStorage.getItem('refreshToken');
};

// Função para salvar tokens (sincronizado com Zustand)
export const saveTokens = (accessToken: string, refreshToken: string) => {
  // Salvar nas chaves diretas (retrocompatibilidade)
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
  
  // Também atualizar o Zustand persist se existir
  const authStorage = localStorage.getItem('auth-storage');
  if (authStorage) {
    try {
      const parsed = JSON.parse(authStorage);
      if (parsed.state) {
        parsed.state.accessToken = accessToken;
        parsed.state.refreshToken = refreshToken;
        parsed.state.tokenExpiry = Date.now() + (60 * 60 * 1000); // 1 hora (igual ao backend)
        localStorage.setItem('auth-storage', JSON.stringify(parsed));
      }
    } catch (e) {
      console.error('Erro ao atualizar tokens no Zustand:', e);
    }
  }
};

// Função para limpar tokens
export const clearTokens = () => {
  // Limpar chaves diretas
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  
  // Limpar também do Zustand persist
  localStorage.removeItem('auth-storage');
};

// Flag para evitar múltiplas tentativas de refresh simultâneas
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

// Processar fila de requisições que falharam durante o refresh
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  
  failedQueue = [];
};

// Interceptor de requisição - adiciona token JWT
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Adicionar header X-Company-Id para escopo de empresa
    const companyId = localStorage.getItem('current_company_id');
    if (companyId && config.headers) {
      config.headers['X-Company-Id'] = companyId;
    }

    // Definir Content-Type apenas se não for FormData
    if (config.data && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    // FormData define seu próprio Content-Type com boundary
    
    // Log de requisições em desenvolvimento (mascarando dados sensíveis)
    if (process.env.NODE_ENV === 'development') {
      const safeData = config.data ? { ...config.data } : undefined;
      if (safeData?.password) safeData.password = '***';
      if (safeData?.token) safeData.token = '***';
      if (safeData?.api_key) safeData.api_key = '***';
      if (safeData?.api_secret) safeData.api_secret = '***';
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, safeData);
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Interceptor de resposta - trata erros e refresh de token
apiClient.interceptors.response.use(
  (response) => {
    // Log de respostas em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Response] ${response.config.url}`, response.data);
    }
    
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest: any = error.config;
    
    // Se erro 401 e não é uma tentativa de refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      
      // Se já estamos fazendo refresh, adicionar à fila
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      const refreshToken = getRefreshToken();
      
      if (!refreshToken) {
        // Sem refresh token, redirecionar para login
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      try {
        // Tentar renovar o token
        const response = await axios.post(`${API_URL}/api/v1/auth/refresh/`, {
          refresh: refreshToken,
        });
        
        const { access } = response.data;
        const currentRefresh = getRefreshToken();
        
        if (access && currentRefresh) {
          saveTokens(access, currentRefresh);
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${access}`;
          originalRequest.headers.Authorization = `Bearer ${access}`;
          
          processQueue(null, access);
          
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // Log de erros em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.error(`[API Error] ${error.config?.url}`, error.response?.data || error.message);
    }
    
    return Promise.reject(error);
  }
);

// Funções auxiliares para requisições
export const api = {
  get: <T = any>(url: string, config?: any) => 
    apiClient.get<ApiResponse<T>>(url, config),
    
  post: <T = any>(url: string, data?: any, config?: any) => 
    apiClient.post<ApiResponse<T>>(url, data, config),
    
  put: <T = any>(url: string, data?: any, config?: any) => 
    apiClient.put<ApiResponse<T>>(url, data, config),
    
  patch: <T = any>(url: string, data?: any, config?: any) => 
    apiClient.patch<ApiResponse<T>>(url, data, config),
    
  delete: <T = any>(url: string, config?: any) => 
    apiClient.delete<ApiResponse<T>>(url, config),
};

// Exportar cliente para uso direto quando necessário
export default apiClient;